import { getApiClient } from "@src/api";
import { ROUTES } from "@src/common/constants";
import { graphql } from "@src/gql/gql";
import { handleError } from "@src/utils/error-handling";
import { showSuccessNotification } from "@src/utils/notifications";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { findBranchKeysContaining } from "../queries/fetch-ranklist";

const cloneWhiteboardQuery = graphql(/* GraphQL */ `
	mutation cloneWhiteboard($whiteboardId: String!, $name: String, $shareKey: String) {
		whiteboards {
			clone(whiteboardId: $whiteboardId, name: $name, shareKey: $shareKey) {
				id
				state {
					name
					ownerId
					status
					permissions {
						publishedLink {
							allowClone
							isReadonly
							key
						}
						userPermissions {
							key
							value
						}
					}
				}
			}
		}
	}
`);

/**
 * Clone a whiteboard.
 *
 * @returns React Query mutation object
 */
export const useCloneWhiteboard = (
	isTemplate: boolean,
	showButton?: boolean,
) => {
	const client = getApiClient();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({
			id,
			name,
			shareKey,
		}: { id: string; name?: string; shareKey?: string }) => {
			const result = await client.mutate({
				mutation: cloneWhiteboardQuery,
				variables: {
					whiteboardId: id,
					name,
					shareKey,
				},
			});

			if (!result.data?.whiteboards?.clone) {
				throw new Error("Failed to clone whiteboard");
			}

			const clonedWhiteboardId = result.data.whiteboards.clone.id;

			if (!clonedWhiteboardId) {
				throw new Error("Failed to clone whiteboard");
			}

			return {
				data: result.data.whiteboards.clone,
				id: clonedWhiteboardId,
			};
		},
		onMutate: async ({ id }) => {
			const sourceBranchKeys = findBranchKeysContaining(queryClient, id);
			return { sourceBranchKeys };
		},
		onSuccess: (result) => {
			if (isTemplate) {
				showSuccessNotification(
					"Success",
					"Whiteboard cloned successfully",
					showButton
						? {
								text: "Go to Whiteboard",
								to: `${ROUTES.whiteboards}/${result.data.id}`,
							}
						: undefined,
				);
			} else {
				showSuccessNotification(
					"Success",
					"Whiteboard duplicated successfully",
				);
			}
		},
		onError: (error) => {
			handleError(
				error,
				isTemplate
					? "Failed to clone whiteboard"
					: "Failed to duplicate whiteboard",
			);
		},
		onSettled: (_data, _error, _vars, context) => {
			for (const queryKey of context?.sourceBranchKeys ?? []) {
				queryClient.invalidateQueries({ queryKey });
			}
		},
	});
};
